import { useCallback, useEffect, useRef, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { hasValidAppToken } from "../utils/appToken";
import {
  getAccessTokenCookie,
  isTokenStale,
  setAccessTokenCookie,
} from "../utils/cookies";
import {
  isUserAuthenticated,
  logoutUser,
  refreshTokenIfNeeded,
} from "../utils/tokenManager";

const AUTH_CALLBACK_PATH = "/auth-callback";
const LANDING_PATH = "/";

interface UseProtectedRouteReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Hook for protecting routes and managing authentication state
 */
export const useProtectedRoute = (): UseProtectedRouteReturn => {
  const { accounts, instance } = useMsal();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  // Authenticated if MSAL has an account, or a valid token exists (handles new tab scenarios)
  const hasCredentials = accounts.length > 0 || isUserAuthenticated();

  /**
   * Result of the last completed `validateAuth`, or null before the first one.
   *
   * `hasCredentials` alone is not sufficient: MSAL can still hold an account
   * while a token refresh fails, so a known-invalid session would otherwise keep
   * rendering protected UI. That mattered less when the guard swapped its whole
   * subtree for a spinner on every navigation; now that it deliberately does not
   * (MDRS-233), the redirect is the only barrier left, so honour the validation
   * result too and withhold children while a session is known-invalid.
   */
  const [validated, setValidated] = useState<boolean | null>(null);

  const isAuthenticated = hasCredentials && validated !== false;

  const validateAuth = useCallback(async () => {
    // External users: a valid app token is a complete session — no MSAL needed.
    if (hasValidAppToken()) return true;

    const validTokenInCookies = isUserAuthenticated();

    if (accounts.length === 0) {
      if (!validTokenInCookies) return false;

      try {
        // Try SSO silent authentication using the token we have
        const msalAccounts = instance.getAllAccounts();
        if (msalAccounts.length > 0) {
          instance.setActiveAccount(msalAccounts[0]);
          return true;
        }

        // No accounts but token exists — try to establish a session (new tab scenario)
        await instance.ssoSilent({ scopes: ["openid", "profile", "email"] });
        const refreshedAccounts = instance.getAllAccounts();
        if (refreshedAccounts.length > 0) {
          instance.setActiveAccount(refreshedAccounts[0]);
          return true;
        }
      } catch (error) {
        console.error("Silent authentication failed:", error);
        // Fall through and attempt to use the existing token
      }

      return validTokenInCookies;
    }

    // Ensure active account is set
    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }

    return await refreshTokenIfNeeded();
  }, [accounts, instance]);

  const [isLoading, setIsLoading] = useState(true);

  // The auth check re-runs on every navigation (pathname is a dependency), but
  // only the FIRST run may gate rendering. Flipping `isLoading` back to true on
  // later runs made `ProtectedRoute` swap its whole subtree for a spinner, which
  // unmounted Layout → RecordingHost → Transcriber and killed any live
  // assessment recording mid-session, silently (MDRS-233). Re-validation now
  // happens in the background with the subtree left mounted; an invalid session
  // still redirects to the landing page below.
  const hasCompletedInitialCheckRef = useRef(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!hasCompletedInitialCheckRef.current) setIsLoading(true);
      const isRedirectFlow = window.location.pathname === AUTH_CALLBACK_PATH;

      try {
        // Handle any pending redirect promise (user returning from login)
        const redirectResponse = await instance.handleRedirectPromise();
        if (redirectResponse?.account) {
          instance.setActiveAccount(redirectResponse.account);
          if (redirectResponse.accessToken) {
            setAccessTokenCookie(redirectResponse.accessToken);
          }
        }

        // Clear expired token if not in a redirect flow. App-issued (external)
        // tokens are exempt from the MSAL 15-min staleness rule — they're valid
        // for their own ~8h lifetime, so only log out once that has expired.
        const token = getAccessTokenCookie();
        const hasAccounts = instance.getAllAccounts().length > 0;
        if (
          token &&
          isTokenStale() &&
          !hasValidAppToken() &&
          !isRedirectFlow &&
          !hasAccounts
        ) {
          await logoutUser(false);
        }

        const isValid = await validateAuth();
        setValidated(isValid);

        if (
          !isValid &&
          !isRedirectFlow &&
          pathname !== LANDING_PATH &&
          pathname !== AUTH_CALLBACK_PATH
        ) {
          const currentPath =
            pathname + window.location.search + window.location.hash;
          sessionStorage.setItem("targetRoute", currentPath);
          navigate({ to: LANDING_PATH, replace: true });
        }
      } catch (error) {
        console.error("Authentication validation error:", error);
        setValidated(false);

        if (!isRedirectFlow) {
          await logoutUser(false);
          if (pathname !== LANDING_PATH && pathname !== AUTH_CALLBACK_PATH) {
            navigate({ to: LANDING_PATH, replace: true });
          }
        }
      } finally {
        hasCompletedInitialCheckRef.current = true;
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [validateAuth, pathname, navigate, instance]);

  return {
    isAuthenticated,
    isLoading,
  };
};
